/**
 * Cache-first helpers: { data, lastUpdated } in localStorage.
 * Step 1: render cached data immediately
 * Step 2: background fetch updates cache + UI
 */

const MAX_AGE_DEFAULT_MS = 24 * 60 * 60 * 1000;

/**
 * @template T
 * @param {string} key
 * @returns {{ data: T | null, lastUpdated: number | null }}
 */
export function readCache(key) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return { data: null, lastUpdated: null };
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && 'data' in parsed) {
      return {
        data: parsed.data ?? null,
        lastUpdated: typeof parsed.lastUpdated === 'number' ? parsed.lastUpdated : null,
      };
    }
  } catch (_) {}
  return { data: null, lastUpdated: null };
}

/**
 * @template T
 * @param {string} key
 * @param {T} data
 */
export function writeCache(key, data) {
  try {
    window.localStorage.setItem(
      key,
      JSON.stringify({ data, lastUpdated: Date.now() })
    );
  } catch (_) {}
}

/**
 * @param {number | null} lastUpdated
 * @param {number} [maxAgeMs]
 */
export function isCacheStale(lastUpdated, maxAgeMs = MAX_AGE_DEFAULT_MS) {
  if (lastUpdated == null) return true;
  return Date.now() - lastUpdated > maxAgeMs;
}

export function cacheAgeLabel(lastUpdated) {
  if (lastUpdated == null) return null;
  const sec = Math.round((Date.now() - lastUpdated) / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  return `${Math.round(min / 60)}h ago`;
}
